// src/components/floorplans/LevelFloorplansManager.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import FloorplanCalibrationModal from "./FloorplanCalibrationModal";

export default function LevelFloorplansManager({
  locationId,
  buildingLevelsAbove,
  buildingLevelsBelow,
  buildingOutline,
  locationCenter,
  onOpenTemplateModal,
}) {
  const [floorplans, setFloorplans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingLevel, setUploadingLevel] = useState(null);
  const [calibratingPlan, setCalibratingPlan] = useState(null);

  // Compute all configured levels sorted from top floor down to basement
  const levelsList = useMemo(() => {
    const list = [];
    const above = parseInt(buildingLevelsAbove, 10);
    const below = parseInt(buildingLevelsBelow, 10);

    // Above ground floors (Ground floor is 0, so if above = 3, levels are 2, 1, 0)
    const maxAbove = !isNaN(above) && above > 0 ? above - 1 : 0;
    for (let i = maxAbove; i >= 1; i--) {
      list.push({
        level: String(i),
        label: `Level ${i} (${i === 1 ? "1st Floor" : i === 2 ? "2nd Floor" : i === 3 ? "3rd Floor" : `${i}th Floor`})`,
        isGround: false,
        isBasement: false,
      });
    }

    // Ground floor (always present if above >= 1 or default)
    list.push({
      level: "0",
      label: "Level 0 (Ground Floor / EG)",
      isGround: true,
      isBasement: false,
    });

    // Below ground floors (e.g. -1, -2)
    const minBelow = !isNaN(below) && below < 0 ? below : 0;
    for (let i = -1; i >= minBelow; i--) {
      list.push({
        level: String(i),
        label: `Level ${i} (Basement ${Math.abs(i)} / UG${Math.abs(i)})`,
        isGround: false,
        isBasement: true,
      });
    }

    return list;
  }, [buildingLevelsAbove, buildingLevelsBelow]);

  // Fetch floorplans from API
  const fetchFloorplans = useCallback(async () => {
    if (!locationId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/main-locations/${locationId}/floorplans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setFloorplans(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch floorplans:", err);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchFloorplans();
  }, [fetchFloorplans]);

  // Handle floorplan file upload for a specific level
  const handleFileUpload = async (level, file) => {
    if (!file || !locationId) return;

    setUploadingLevel(level);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("level", level);
    formData.append("name", `Level ${level} Floorplan`);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/main-locations/${locationId}/floorplans`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Upload failed.");
      }

      Swal.fire({
        icon: "success",
        title: "Floorplan Uploaded!",
        text: `Plan for Level ${level} uploaded. Would you like to adjust its coordinates on the map now?`,
        showCancelButton: true,
        confirmButtonText: "Adjust Coordinates Now",
        cancelButtonText: "Later",
        confirmButtonColor: "#487fff",
      }).then((result) => {
        if (result.isConfirmed) {
          setCalibratingPlan(data.data);
        }
      });

      await fetchFloorplans();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Upload Error",
        text: err.message,
      });
    } finally {
      setUploadingLevel(null);
    }
  };

  // Handle floorplan deletion
  const handleDeletePlan = async (plan) => {
    const result = await Swal.fire({
      title: "Delete Floorplan?",
      text: `Are you sure you want to remove the floorplan for Level ${plan.level}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`/api/main-locations/${locationId}/floorplans/${plan.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || "Failed to delete floorplan.");
        }

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: `Floorplan for Level ${plan.level} was removed.`,
          timer: 1500,
          showConfirmButton: false,
        });

        await fetchFloorplans();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.message,
        });
      }
    }
  };

  if (!locationId) {
    return (
      <div className="alert alert-neutral p-16 radius-8 border text-secondary-light text-xs d-flex align-items-center gap-2">
        <Icon icon="solar:info-circle-linear" className="text-lg flex-shrink-0" />
        <div>
          Please save this location first. Once created, you will be able to upload and georeference floorplans for every level.
        </div>
      </div>
    );
  }

  const hasOutline = Array.isArray(buildingOutline) && buildingOutline.length >= 3;

  return (
    <div className="d-flex flex-column gap-16">
      {/* Header Info */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h6 className="text-sm fw-semibold mb-1 text-dark d-flex align-items-center gap-2">
            <Icon icon="solar:map-arrow-square-linear" className="text-primary-600 text-lg" />
            Level Floorplans & Georeferencing
          </h6>
          <p className="text-xs text-secondary-light mb-0">
            Upload architectural floorplans or schematics for each level and adjust them directly over the building coordinates.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {onOpenTemplateModal && hasOutline && (
            <button
              type="button"
              className="btn btn-outline-primary radius-6 d-inline-flex align-items-center gap-1"
              style={{ fontSize: "12px", height: "30px", padding: "0 10px", lineHeight: "28px" }}
              onClick={onOpenTemplateModal}
              title="Download a scaled building outline template image to draw your floorplan"
            >
              <Icon icon="solar:download-square-linear" style={{ fontSize: "14px" }} />
              <span>Download Template Image</span>
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary radius-6 d-inline-flex align-items-center gap-1"
            style={{ fontSize: "12px", height: "30px", padding: "0 10px", lineHeight: "28px" }}
            onClick={fetchFloorplans}
            disabled={loading}
            title="Refresh floorplan status"
          >
            <Icon icon="solar:refresh-linear" className={loading ? "spin" : ""} style={{ fontSize: "14px" }} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* List of Levels */}
      <div className="row g-3">
        {levelsList.map((lvl) => {
          const plan = floorplans.find((p) => String(p.level) === String(lvl.level));
          const isUploading = uploadingLevel === lvl.level;

          return (
            <div key={lvl.level} className="col-xl-6 col-md-12">
              <div
                className={`p-14 radius-10 border transition-2 ${
                  plan ? "bg-white border-primary-200 shadow-sm" : "bg-neutral-50 border-dashed"
                }`}
              >
                {/* Level Title Bar */}
                <div className="d-flex align-items-center justify-content-between mb-12 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span
                      className={`badge text-xs px-8 py-4 radius-6 ${
                        lvl.isGround
                          ? "bg-success-50 text-success-700 border border-success-200"
                          : lvl.isBasement
                          ? "bg-warning-50 text-warning-700 border border-warning-200"
                          : "bg-primary-50 text-primary-700 border border-primary-200"
                      }`}
                    >
                      {lvl.label}
                    </span>
                    {plan && (
                      <span className="badge bg-success-50 text-success-700 border border-success-200 text-xxs d-inline-flex align-items-center gap-1 py-4 px-6 radius-4">
                        <Icon icon="solar:check-circle-bold" className="text-success-600" /> Georeferenced
                      </span>
                    )}
                  </div>

                  {plan && (
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary radius-6 d-inline-flex align-items-center gap-1"
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          height: "28px",
                          padding: "0 10px",
                          lineHeight: "26px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => setCalibratingPlan(plan)}
                        title="Adjust coordinate bounds, rotation, and alignment"
                      >
                        <Icon icon="solar:ruler-angular-bold" style={{ fontSize: "14px" }} />
                        <span>Adjust Coordinates</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger radius-6 d-inline-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: "28px", height: "28px", padding: 0 }}
                        onClick={() => handleDeletePlan(plan)}
                        title="Delete floorplan"
                      >
                        <Icon icon="solar:trash-bin-trash-linear" style={{ fontSize: "14px" }} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Plan Content */}
                {plan ? (
                  <div className="d-flex align-items-center gap-12">
                    {/* Thumbnail Preview */}
                    <div
                      className="radius-8 overflow-hidden border bg-neutral-100 flex-shrink-0 position-relative cursor-pointer shadow-none"
                      style={{ width: "90px", height: "70px" }}
                      onClick={() => setCalibratingPlan(plan)}
                      title="Click to calibrate coordinates"
                    >
                      <img
                        src={plan.image_url}
                        alt={`Level ${plan.level} plan`}
                        className="w-100 h-100 object-fit-cover"
                      />
                      <div className="position-absolute bottom-0 start-0 end-0 bg-neutral-900 bg-opacity-70 text-white text-xxs py-1 text-center font-monospace">
                        Rot: {Math.round(plan.rotation || 0)}°
                      </div>
                    </div>

                    {/* Metadata & Coordinate summary */}
                    <div className="flex-grow-1 min-w-0">
                      <div className="text-xs fw-semibold text-dark text-truncate mb-4">
                        {plan.name || `Level ${plan.level} Floorplan`}
                      </div>
                      <div
                        className="font-monospace text-secondary-light px-8 py-4 radius-6 border bg-neutral-50"
                        style={{ fontSize: "10.5px", lineHeight: "1.4" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>N: {parseFloat(plan.north).toFixed(6)}</span>
                          <span className="text-neutral-300">|</span>
                          <span>S: {parseFloat(plan.south).toFixed(6)}</span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                          <span>E: {parseFloat(plan.east).toFixed(6)}</span>
                          <span className="text-neutral-300">|</span>
                          <span>W: {parseFloat(plan.west).toFixed(6)}</span>
                        </div>
                      </div>
                      <div className="mt-6 d-flex align-items-center gap-2">
                        {/* Replace image input */}
                        <label className="btn btn-link p-0 text-xxs text-primary-600 cursor-pointer mb-0 fw-medium">
                          Replace Image
                          <input
                            type="file"
                            accept="image/*"
                            className="d-none"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(lvl.level, e.target.files[0]);
                              }
                            }}
                          />
                        </label>
                        <span className="text-neutral-300">•</span>
                        <a
                          href={plan.image_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xxs text-secondary-light hover-text-primary"
                        >
                          View Full Image
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Empty state: Upload Dropzone */
                  <div>
                    <label
                      className={`w-100 p-12 radius-8 border border-dashed d-flex align-items-center justify-content-center flex-column gap-1 cursor-pointer mb-0 transition-2 ${
                        isUploading ? "bg-primary-50 border-primary" : "bg-white hover-bg-neutral-100"
                      }`}
                    >
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/webp, image/svg+xml"
                        className="d-none"
                        disabled={isUploading}
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleFileUpload(lvl.level, e.target.files[0]);
                          }
                        }}
                      />
                      {isUploading ? (
                        <div className="d-flex align-items-center gap-2 text-primary text-xs py-4">
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          Uploading & generating georeference...
                        </div>
                      ) : (
                        <>
                          <div className="d-flex align-items-center gap-1 text-primary-600 text-xs fw-semibold">
                            <Icon icon="solar:upload-track-linear" className="text-base" />
                            Upload Floorplan for {lvl.label}
                          </div>
                          <span className="text-xxs text-secondary-light">
                            Supports PNG, JPG, SVG, WebP (Max 50MB)
                          </span>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Coordinate Calibration Modal */}
      {calibratingPlan && (
        <FloorplanCalibrationModal
          show={!!calibratingPlan}
          floorplan={calibratingPlan}
          locationId={locationId}
          buildingOutline={buildingOutline}
          locationCenter={locationCenter}
          onClose={() => setCalibratingPlan(null)}
          onSaveSuccess={(updatedPlan) => {
            setFloorplans((prev) =>
              prev.map((p) => (p.id === updatedPlan.id ? updatedPlan : p))
            );
          }}
        />
      )}
    </div>
  );
}

